import React, { Component } from 'react';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { twMerge } from 'tailwind-merge';

import { searchOrigins } from '../../Navigation/constants';
import { SearchFilterContentTypes } from '../../Shared/enums';
import { updateSearchOptionsAndRefresh } from '../actions/SearchActions';
import HumanMadeCalloutBanner from '../components/HumanMadeCalloutBanner';
import ToggleMenuButton from '../components/ToggleMenuButton';
import ClearFilters from '../components/filters/ClearFilters';
import CollapsibleCheckboxFilter from '../components/filters/CollapsibleCheckboxFilter';
import CollapsibleRadioFilter from '../components/filters/CollapsibleRadioFilter';
import ColorFilter from '../components/filters/ColorFilter';
import {
  fileTypes,
  getCategoriesForContentType,
  mediaDetails,
  orientations,
} from '../entities/ImageSearchFilterOptions';
import SearchFilterOptions, {
  ALL_IMAGES_CONTENT_TYPE,
} from '../entities/SearchFilterOptions';
import SearchOptions from '../entities/SearchOptions';
import { UsageRights, UsageRightsTooltip } from '../entities/UsageRights';
import {
  selectSearchFilterOptions,
  selectShouldUpdateSearchFilters,
  selectShowHumanMadeContent,
} from '../selectors/searchSelectors';
import { ImageSelectedSearchFilterOptions } from './MenuContainerInterfaces';

interface Props {
  shouldOpenSideMenu: boolean;
  closeSideMenu: () => void;
  showSideMenu: () => void;
  selectedSearchFilterOptions: ImageSelectedSearchFilterOptions;
  dispatch: ThunkDispatch<any, any, any>;
  showHumanMadeContent: boolean;
}

interface State {
  collapsed: Collapsed;
}

interface Collapsed {
  categories: boolean;
  contentType: boolean;
  orientation: boolean;
  color: boolean;
  mediaDetails: boolean;
  fileType: boolean;
  usageRights: boolean;
}

class ImageMenuContainer extends Component<Props, State> {
  state = {
    collapsed: {
      categories: false,
      contentType: false,
      orientation: false,
      color: false,
      mediaDetails: false,
      fileType: false,
      usageRights: false,
    },
  };

  shouldComponentUpdate(nextProps) {
    return nextProps.shouldUpdateSearchFilters !== false;
  }

  render() {
    const closeSideMenuClass = 'close-side-menu w-0';
    const sideMenuClass = twMerge(
      'graphicstock side-menu block',
      this.props.shouldOpenSideMenu ? 'open-side-menu' : closeSideMenuClass
    );

    return (
      <div className={sideMenuClass}>
        <div className="side-menu-stickyContainer">
          <ClearFilters
            isOpen={this.props.shouldOpenSideMenu}
            closeSideMenu={this.props.closeSideMenu}
            showSideMenu={this.props.showSideMenu}
            clearFilters={this.clearFilters}
          />
          <div className="side-menu-filters">
            {this.props.showHumanMadeContent && <HumanMadeCalloutBanner />}

            {this.renderContentTypeFilter()}

            {this.renderOrientationFilter()}

            {this.renderColorFilter()}

            {this.renderMediaDetailsFilter()}

            {this.renderUsageRightsFilter()}

            {this.renderCategoryFilter()}

            {this.renderFileTypeFilter()}
          </div>
        </div>
      </div>
    );
  }

  renderToggleMenuButton() {
    return (
      <ToggleMenuButton
        isOpen={this.props.shouldOpenSideMenu}
        closeSideMenu={this.props.closeSideMenu}
        showSideMenu={this.props.showSideMenu}
      />
    );
  }

  renderContentTypeFilter() {
    return (
      <React.Fragment>
        <CollapsibleRadioFilter
          checked={this.props.selectedSearchFilterOptions.contentType}
          filterName="contentType"
          filterValues={SearchFilterOptions.getContentTypes()}
          handleFilterChanged={this.handleFilterChanged}
          name="Media Type"
          isCollapsed={this.state.collapsed.contentType}
          toggleCollapsed={() => this.toggleCollapsed('contentType')}
        />
      </React.Fragment>
    );
  }

  renderOrientationFilter() {
    return (
      <React.Fragment>
        <CollapsibleRadioFilter
          name="Orientation"
          handleFilterChanged={this.handleFilterChanged}
          filterName="orientation"
          filterValues={orientations}
          checked={this.props.selectedSearchFilterOptions.orientation}
          alphabetizeOptions
          isCollapsed={this.state.collapsed.orientation}
          toggleCollapsed={() => this.toggleCollapsed('orientation')}
        />
      </React.Fragment>
    );
  }

  renderColorFilter() {
    return (
      <React.Fragment>
        <ColorFilter
          name="Color"
          filterName="color"
          value={this.props.selectedSearchFilterOptions.color || ''}
          onChange={this.handleFilterChanged}
          isCollapsed={this.state.collapsed.color}
          toggleCollapsed={() => this.toggleCollapsed('color')}
        />
      </React.Fragment>
    );
  }

  renderMediaDetailsFilter() {
    const selectedMediaDetails = this.getSelectedMediaDetails(
      this.props.selectedSearchFilterOptions
    );

    return (
      <React.Fragment>
        <CollapsibleCheckboxFilter
          name="Media Details"
          handleFilterChanged={this.handleFilterChanged}
          groupId="MediaDetails"
          options={mediaDetails}
          checkedValues={selectedMediaDetails}
          isCollapsed={this.state.collapsed.mediaDetails}
          toggleCollapsed={() => this.toggleCollapsed('mediaDetails')}
        />
      </React.Fragment>
    );
  }

  renderUsageRightsFilter() {
    const selectedUsageRights =
      this.props.selectedSearchFilterOptions.usageRights ?? UsageRights.All;

    const allOptions = [
      {
        label: 'All usage rights',
        value: UsageRights.All,
      },
      {
        label: 'Commercial',
        value: UsageRights.Commercial,
        tooltip: UsageRightsTooltip.Commercial,
      },
      {
        label: 'Editorial',
        value: UsageRights.Editorial,
        tooltip: UsageRightsTooltip.Editorial,
      },
    ];

    return (
      <>
        <CollapsibleRadioFilter
          name="Usage Rights"
          filterName="usageRights"
          filterValues={allOptions}
          checked={selectedUsageRights}
          isCollapsed={this.state.collapsed.usageRights}
          toggleCollapsed={() => this.toggleCollapsed('usageRights')}
          alphabetizeOptions={false}
          handleFilterChanged={this.handleFilterChanged}
        />
      </>
    );
  }

  renderCategoryFilter() {
    const { selectedSearchFilterOptions } = this.props;
    const currentCategoryOptions = getCategoriesForContentType(
      selectedSearchFilterOptions.contentType
    );
    const isAllImages =
      selectedSearchFilterOptions.contentType === ALL_IMAGES_CONTENT_TYPE;

    return (
      <>
        <CollapsibleRadioFilter
          name="Categories"
          handleFilterChanged={this.handleFilterChanged}
          filterName="categories"
          filterValues={currentCategoryOptions}
          checked={selectedSearchFilterOptions.categories}
          alphabetizeOptions
          invalidSelectionMessage={
            isAllImages ? 'Select Media Type to see Categories' : null
          }
          isCollapsed={this.state.collapsed.categories}
          toggleCollapsed={() => this.toggleCollapsed('categories')}
        />
      </>
    );
  }

  renderFileTypeFilter() {
    return (
      <React.Fragment>
        <CollapsibleCheckboxFilter
          name="File Type"
          handleFilterChanged={this.handleFilterChanged}
          groupId="FileType"
          options={fileTypes}
          checkedValues={
            this.props.selectedSearchFilterOptions.hasTransparency
              ? ['hasTransparency']
              : []
          }
          isCollapsed={this.state.collapsed.fileType}
          toggleCollapsed={() => this.toggleCollapsed('fileType')}
        />
      </React.Fragment>
    );
  }

  getSelectedMediaDetails(selectedSearchFilterOptions) {
    const selectedMediaDetails = [];

    if (selectedSearchFilterOptions.propertyReleased) {
      selectedMediaDetails.push('propertyReleased');
    }

    if (selectedSearchFilterOptions.talentReleased) {
      selectedMediaDetails.push('talentReleased');
    }

    return selectedMediaDetails;
  }

  handleFilterChanged = (name: string, value?: SearchFilterContentTypes) => {
    const contentTypeDidChange =
      name === 'contentType' &&
      value !== this.props.selectedSearchFilterOptions.contentType;

    const propertiesToChange: Partial<ImageSelectedSearchFilterOptions> = {
      [name]: value,
      page: 1, // Go back to page 1 when any filter is changed
      searchOrigin: searchOrigins.FILTERS,
      isPagination: false,
    };

    if (name === 'contentType') {
      propertiesToChange.contentType = value;

      // Reassert the content class in case the NavSearchContainer selection has changed
      propertiesToChange.contentClass =
        SearchFilterOptions.getContentClassFromContentType(value);
    }

    if (contentTypeDidChange) {
      // Reset category
      // @ts-expect-error need to resolve whether categories is a string or string[]...
      propertiesToChange.categories = '';
    }

    if (this.props.selectedSearchFilterOptions.contentType) {
      propertiesToChange.contentClass =
        SearchFilterOptions.getContentClassFromContentType(
          this.props.selectedSearchFilterOptions.contentType
        );
    }

    const newSearchOptions =
      this.props.selectedSearchFilterOptions.update(propertiesToChange);
    this.props.dispatch(updateSearchOptionsAndRefresh(newSearchOptions));
  };

  clearFilters = (e) => {
    e.preventDefault();

    let newSearchOptions = SearchOptions.getDefaultSearchOptions();
    newSearchOptions = newSearchOptions.update({
      searchTerm: this.props.selectedSearchFilterOptions.searchTerm,
    });

    this.props.dispatch(updateSearchOptionsAndRefresh(newSearchOptions));

    this.setState({
      collapsed: {
        ...this.state.collapsed,
        categories: false,
      },
    });
  };

  toggleCollapsed(name) {
    const collapsed = this.state.collapsed;
    collapsed[name] = !collapsed[name];

    this.setState({ collapsed });
  }
}

function mapStateToProps(state) {
  return {
    selectedSearchFilterOptions: selectSearchFilterOptions(state),
    shouldUpdateSearchFilters: selectShouldUpdateSearchFilters(state),
    showHumanMadeContent: Boolean(selectShowHumanMadeContent(state)),
  };
}

export default connect(mapStateToProps)(ImageMenuContainer);
